import { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useStateValue } from "../context/state/provider";
import {
	AppBar,
	Toolbar,
	Tooltip,
} from "@material-ui/core";
import UserModal from "../components/UserModal";
import HeaderTitle from "../components/HeaderTitle";
import TextIcon from "../images/textIcon.svg";
import RefreshIcon from "../images/refreshIcon.svg";
import { VIEW } from "../constants/view.constants";
import { auth } from "../services/firebase/firebase";
import writeGreetingsOptions from "../helpers/writeGreetingsOptions";
import writeForm from "../helpers/writeForm"
import { useAuthState } from "react-firebase-hooks/auth";

const useStyles = makeStyles({
	wrap: {
		flexShrink: 0,
		position: "relative",
		zIndex: 2,
	},
	brandBar: {
		height: 3,
		width: "100%",
		background: "var(--active-brand-color)",
		borderRadius: (props) =>
			props.isFullView ? 0 : "var(--radius-shell) var(--radius-shell) 0 0",
	},
	appbar: {
		position: "relative !important",
		width: "100%",
		color: "var(--color-dark-grey)",
		height: "var(--header-height)",
		minHeight: "var(--header-height)",
		padding: "0 16px 0 18px",
		borderRadius: 0,
		transition: "background-color 0.25s ease",
		boxShadow: "none !important",
		flexShrink: 0,
		borderBottom: "1px solid var(--surface-border)",
		backgroundColor: "var(--surface-bg) !important",
	},
	'@media (max-height: 180px)': {
		appbar: {
			display: "none",
		},
		brandBar: {
			display: "none",
		},
	},
	titleSlot: {
		flex: "1 1 auto",
		minWidth: 0,
		overflow: "hidden",
	},
	actions: {
		display: "flex",
		alignItems: "center",
		flex: "0 0 auto",
		gap: 4,
		marginLeft: 8,
	},
	icon: {
		height: "20px",
		width: "20px",
		// White SVGs → dark icons for light header
		filter: "brightness(0) saturate(100%) invert(18%) sepia(10%) saturate(900%) hue-rotate(176deg)",
		opacity: 0.85,
	},
	toolbar: {
		padding: "0 !important",
		minHeight: "var(--header-height) !important",
		gap: "8px",
		width: "100%",
	},
	iconButton: {
		padding: "8px",
		alignItems: "center",
		justifyContent: "center",
		flex: "0 0 auto",
		display: "inline-flex",
		border: 0,
		cursor: "pointer",
		borderRadius: "10px",
		backgroundColor: "transparent",
		outline: 0,
		transition: "background-color 150ms ease",
		"&:hover": {
			background: "rgba(15, 23, 42, 0.06)",
		},
		"&:disabled": {
			opacity: 0.35,
			cursor: "default",
			pointerEvents: "none",
		},
		"&:focus-visible": {
			boxShadow: "0 0 0 2px rgba(11, 79, 156, 0.35)",
		},
	},
	iconButtonDisabled: {
		padding: "8px",
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		border: 0,
		borderRadius: "10px",
		background: "transparent",
		opacity: 0.35,
		cursor: "default",
		pointerEvents: "none",
	},
	a11yBtn: {
		display: "inline-flex",
		alignItems: "center",
		gap: 6,
		flexShrink: 0,
		border: "1px solid var(--surface-border)",
		background: "var(--color-alt-grey)",
		color: "var(--color-dark-grey)",
		fontFamily: "var(--font-sans)",
		fontSize: 12,
		fontWeight: 700,
		letterSpacing: "-0.01em",
		padding: "7px 10px",
		borderRadius: 10,
		cursor: "pointer",
		lineHeight: 1,
		transition: "background 0.15s ease, border-color 0.15s ease",
		"&:hover": {
			background: "#e2e8f0",
			borderColor: "#cbd5e1",
		},
		"&:disabled": {
			opacity: 0.55,
			cursor: "default",
		},
		"& img": {
			height: 15,
			width: 15,
		},
	},
	a11yBtnActive: {
		background: "rgba(11, 79, 156, 0.1)",
		borderColor: "var(--active-brand-color)",
		color: "var(--active-brand-color)",
	},
	loginBtn: {
		marginLeft: 8,
		flexShrink: 0,
		border: "1px solid var(--active-brand-color)",
		background: "var(--active-brand-color)",
		color: "#fff",
		fontFamily: "var(--font-sans)",
		fontSize: 13,
		fontWeight: 600,
		letterSpacing: "-0.01em",
		padding: "8px 14px",
		borderRadius: 12,
		cursor: "pointer",
		whiteSpace: "nowrap",
		lineHeight: 1.2,
		transition: "opacity 0.15s ease, transform 0.15s ease",
		"&:hover": {
			opacity: 0.92,
		},
		"&:disabled": {
			opacity: 0.45,
			cursor: "default",
		},
	},
	loginBtnActive: {
		background: "var(--color-alt-grey)",
		borderColor: "var(--surface-border)",
		color: "var(--color-dark-grey)",
		"&:hover": {
			background: "#e2e8f0",
			opacity: 1,
		},
	},
});

function extractTokenFromUrl() {
	// Get the URL query parameters
	const queryString = window.location.search;

	// Parse the query string into an object
	const queryParams = new URLSearchParams(queryString);

	// Get the value of the 'token' parameter
	const token = queryParams.get('token');
	console.log(token);
	return token;
}

export default function Navbar({ user, brandingTitle, subtitle, avatarUrl, firestore, chatbotId, isFullView = true }) {
	const classes = useStyles({ isFullView });
	const [{ view }, dispatch] = useStateValue(); // user,
	const [modalOpen, setModalOpen] = useState(false);
	const [authState, authLoading, authError] = useAuthState(auth);

	const handleAccessibilityButton = () => {
		dispatch({
			type: "SET_VIEW",
			view: VIEW.ACCESSIBILITY,
		});
	};

	const handleClickOpen = () => {
		setModalOpen(true);
	};

	const handleClose = (value) => {
		if (view === VIEW.CHATROOM) {
		}
		setModalOpen(false);
	};

	const handleLogout = () => {
		console.log("handleLogout")
		//Set logginOut state to hide input field and error message if any while the logout and fetch of new greeting message is completed
		dispatch({
			type: "LOGGING_OUT",
			loggingOut: true,
		});

		sessionStorage.setItem("prevProvider", JSON.parse(sessionStorage.getItem("CLIENT")).defaultAgent);
		auth.signOut()
			.then(() => {
				console.log("Signout Successful");

				dispatch({
					type: "ADMIN_SETTINGS",
					admin: {
						adminUser: false,
						feedbackOn: false,
					},
				});
				localStorage.removeItem("userType");

				//Before closing the modal login as anonymous user
				auth.signInAnonymously()
					.then((currentUser) => {
						console.log({ currentUser });
						const snapShot = async () => {
							await firestore
								.collection("rooms")
								.doc(currentUser.user.uid + "***" + chatbotId)
								.get()
						}

						if (!snapShot.exists) {
							firestore
								.collection("rooms")
								.doc(currentUser.user.uid)
								.set({
									chatbotId: chatbotId,
									// provider: JSON.parse(sessionStorage?.getItem("CLIENT"))?.defaultAgent,
									// providerId: JSON.parse(sessionStorage?.getItem("CLIENT"))?.defaultAgentId,
								}, { merge: true })
							// .then(() => {
							// 	//get chatbot info to check in the form need to be display immediatly
							// 	firestore
							// 		.collection("chatbots")
							// 		.doc(chatbotId)
							// 		.get()
							// 		.then(async (res) => {
							// 			const chatbotDocVal = res.data()
							// 			console.log("GET CHATBOT INFO", chatbotDocVal);
							// 			//If the trigger for form is first request
							// 			console.log("WRITE GREETINGS NAVBAR.js line 230")
							// 			if (chatbotDocVal.trigger === "After First Request") {
							// 				writeForm(currentUser.user.uid, chatbotId)
							// 			} else {
							// 				writeGreetingsOptions(currentUser.user.uid, chatbotId)
							// 			}
							// 		})
							// 		.catch((err) => {
							// 			console.log("ERROR", err);
							// 		});
							// })
							// .catch((error) => {
							// 	console.error("Error writing document: ", error);
							// });
						}
						//check userType in local storage
						localStorage.removeItem("userType");

						dispatch({
							type: "USER",
							user: {
								...user,
								id: currentUser.user.uid,
								loggedIn: false,
								name: null,
							},
						});

						dispatch({
							type: "SET_VIEW",
							view: VIEW.CHATROOM,
						});
					})
					.catch((err) => {
						console.log("ERROR", err);
					});
			})
			.catch((err) => {
				console.error("signout error: ", err);
			});
	};

	const handleRefreshRoom = async () => {
		try {
			console.log("Starting handleRefreshRoom...");
			const loggedInUid = auth.currentUser.uid;
			const dateRefresh = new Date().toISOString().replace(/[:.]/g, '-'); // Format date safely for Firestore path
			console.log("dateRefresh:", dateRefresh);
			console.log("loggedInUid:", loggedInUid);
			console.log("chatbotId:", chatbotId);
			const roomRef = firestore.collection("rooms").doc(loggedInUid + "***" + chatbotId);

			// Get existing messages
			const oldMessagesSnapshot = await roomRef.collection("messages").get();

			if (oldMessagesSnapshot.empty) {
				console.log("No messages to move.");
				return;
			}

			console.log(`Found ${oldMessagesSnapshot.size} messages to copy and delete.`);

			// Prepare batch operations
			const batch = firestore.batch();

			// Copy messages to new collection and delete from old collection
			oldMessagesSnapshot.forEach((doc) => {
				const newDocRef = roomRef.collection(`messages-${dateRefresh}`).doc(doc.id);
				batch.set(newDocRef, doc.data()); // Copy the document to the new collection

				const oldDocRef = roomRef.collection("messages").doc(doc.id);
				batch.delete(oldDocRef); // Delete the document from the old collection
			});

			// Commit batch operations
			await batch.commit();
			console.log("Batch commit complete: Documents copied and deleted.");

			// Proceed with the rest of the code
			const chatbotDoc = await firestore.collection("chatbots").doc(chatbotId).get();
			const chatbotDocVal = chatbotDoc.data();
			console.log("GET CHATBOT INFO", chatbotDocVal);

			// Call appropriate function based on trigger
			console.log("WRITE GREETINGS NAVBAR.js line 314")
			if (chatbotDocVal.trigger === "After First Request") {
				await writeForm(loggedInUid, chatbotId);
			} else {
				await writeGreetingsOptions(loggedInUid, chatbotId);
			}

			// Update pendingMessages
			await roomRef.set({ pendingMessages: 0 }, { merge: true });
			console.log("Pending messages set to 0.");

			// Dispatch action to set view
			dispatch({
				type: "SET_VIEW",
				view: VIEW.CHATROOM,
			});
		} catch (error) {
			console.error("Error in handleRefreshRoom:", error);
		}
	};

	// token used onely in app-open-v1
	const token = extractTokenFromUrl();

	const isLoggedIn = authState && !authState.isAnonymous;
	const loginLabel = isLoggedIn ? "Account" : "Sign in";

	return (
		<div className={classes.wrap}>
			<div className={classes.brandBar} aria-hidden="true" />
			<AppBar className={classes.appbar} position="relative" elevation={0}>
				<Toolbar className={classes.toolbar} disableGutters>
					<header className={classes.titleSlot}>
						<HeaderTitle brandingTitle={brandingTitle} subtitle={subtitle} avatarUrl={avatarUrl} />
					</header>
					<nav className={classes.actions} aria-label="Chat actions">
						<Tooltip title="Refresh conversation">
							<button
								type="button"
								className={authState?.loggedIn ? classes.iconButtonDisabled : classes.iconButton}
								disabled={authState?.loggedIn}
								aria-label="Refresh conversation"
								onClick={() => {
									handleRefreshRoom();
									document.activeElement.blur();
								}}
							>
								<img src={RefreshIcon} className={classes.icon} alt="" aria-hidden="true" />
							</button>
						</Tooltip>
						<Tooltip title="Accessibility settings">
							<button
								type="button"
								className={`${classes.a11yBtn} ${view === VIEW.ACCESSIBILITY ? classes.a11yBtnActive : ""}`}
								disabled={view === VIEW.ACCESSIBILITY}
								aria-label="Accessibility settings"
								onClick={() => {
									if (view !== VIEW.ACCESSIBILITY) handleAccessibilityButton();
									document.activeElement.blur();
								}}
							>
								<img src={TextIcon} className={classes.icon} alt="" aria-hidden="true" />
								<span>A11y</span>
							</button>
						</Tooltip>
						{!token && (
							<button
								type="button"
								className={`${classes.loginBtn} ${isLoggedIn ? classes.loginBtnActive : ""}`}
								disabled={modalOpen || view === VIEW.LOGIN}
								onClick={() => {
									handleClickOpen();
									document.activeElement.blur();
								}}
								aria-label={loginLabel}
							>
								{loginLabel}
							</button>
						)}
					</nav>
				</Toolbar>
			</AppBar>
			<UserModal
				firestore={firestore}
				setModalOpen={setModalOpen}
				open={modalOpen}
				onClose={handleClose}
				userDB={user}
				chatbotId={chatbotId}
			/>
		</div>
	);
}
